import { createSlice, PayloadAction } from '@reduxjs/toolkit'

const LS_KEY = 'loginThrottle'

interface LoginThrottleState {
  failedAttempts: number
  blockedUntil: number | null // timestamp in ms
}

const defaultState: LoginThrottleState = {
  failedAttempts: 0,
  blockedUntil: null
}

const readFromLocalStorage = (): LoginThrottleState => {
  try {
    const data = localStorage.getItem(LS_KEY)
    return data ? JSON.parse(data) : defaultState
  } catch {
    return defaultState
  }
}

const writeToLocalStorage = (state: LoginThrottleState) => {
  localStorage.setItem(LS_KEY, JSON.stringify(state))
}

const loginThrottleSlice = createSlice({
  name: 'loginThrottle',
  initialState: readFromLocalStorage(),
  reducers: {
    incrementFailedAttempts(state) {
      state.failedAttempts += 1

      if (state.failedAttempts >= 5) {
        const blockMinutes = Math.min(state.failedAttempts - 4, 5)
        state.blockedUntil = Date.now() + blockMinutes * 60 * 1000
      }

      writeToLocalStorage(state)
    },
    resetLoginThrottle(state) {
      state.failedAttempts = 0
      state.blockedUntil = null
      writeToLocalStorage(state)
    },
    manuallySetThrottleState(state, action: PayloadAction<LoginThrottleState>) {
      state.failedAttempts = action.payload.failedAttempts
      state.blockedUntil = action.payload.blockedUntil
      writeToLocalStorage(state)
    }
  }
})

export const {
  incrementFailedAttempts,
  resetLoginThrottle,
  manuallySetThrottleState
} = loginThrottleSlice.actions

export default loginThrottleSlice.reducer
